/**
 * Profilfeld-Platzhalter und ihre beiden Zusatzmarkierungen (wie im DN365-Portal):
 *
 * - Hide line: `{{Telefon|HL}}` — ist der Wert leer, verschwindet die
 *   ganze Zeile samt Beschriftung davor. Altbestand mit `|RF` wird weiterhin gelesen.
 * - Conditional range: `[#Tel.: {{Telefon}}#]` — ist ein Platzhalter innerhalb der
 *   Klammern leer, verschwindet der komplette eingeklammerte Bereich.
 *
 * Dieselben Regeln setzt das Backend beim Rendern um (ProfilePlaceholderRenderer.cs).
 */

export const HIDE_FIELD_OPEN = "[#";
export const HIDE_FIELD_CLOSE = "#]";
/** Aktuelles Kürzel für Hide line. */
export const HIDE_LINE_SUFFIX = "|HL";

/** Ein Platzhalter mit optionaler Hide-line-Endung (`|HL`, Altbestand `|RF`). */
const TOKEN_PATTERN = /\{\{([^{}|]+?)(\|(?:HL|RF))?\}\}/g;

/** Frische Regex-Instanz, damit sich Aufrufer nicht über lastIndex in die Quere kommen. */
function profileTokenRegex(): RegExp {
  return new RegExp(TOKEN_PATTERN.source, "g");
}

export interface ParsedProfileToken {
  /** Vollständiger Text, z. B. "{{Telefon|HL}}". */
  token: string;
  /** Feldname ohne Klammern, z. B. "Telefon". */
  name: string;
  hideLine: boolean;
}

/** Prüft einen Text darauf, ob er genau einem Platzhalter entspricht. */
export function parseProfileToken(text: string): ParsedProfileToken | null {
  const match = /^\{\{([^{}|]+?)(\|(?:HL|RF))?\}\}$/.exec(text.trim());
  if (!match) return null;
  return { token: match[0], name: match[1].trim(), hideLine: !!match[2] };
}

export function buildProfileToken(name: string, hideLine: boolean): string {
  return hideLine ? `{{${name}${HIDE_LINE_SUFFIX}}}` : `{{${name}}}`;
}

/** Wert eines Feldes; unbekannte oder leere Felder ergeben "". */
function valueOf(values: Record<string, string>, name: string): string {
  return values[`{{${name}}}`] ?? values[`{{${name.trim()}}}`] ?? "";
}

interface TextSpan {
  node: Text;
  start: number;
  end: number;
}

/** Alle Textknoten in Dokumentreihenfolge mit ihren Positionen im Gesamttext. */
function flattenText(root: HTMLElement): { text: string; spans: TextSpan[] } {
  const spans: TextSpan[] = [];
  let text = "";
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let node: Node | null = walker.nextNode();
  while (node) {
    const textNode = node as Text;
    const data = textNode.data ?? "";
    spans.push({ node: textNode, start: text.length, end: text.length + data.length });
    text += data;
    node = walker.nextNode();
  }
  return { text, spans };
}

/** Entfernt den Bereich [from, to) aus dem Gesamttext; leere Elemente bleiben stehen. */
function deleteFlatRange(spans: TextSpan[], from: number, to: number): void {
  // Von hinten nach vorn, damit die bereits berechneten Positionen gültig bleiben.
  for (let i = spans.length - 1; i >= 0; i -= 1) {
    const span = spans[i];
    const start = Math.max(from, span.start);
    const end = Math.min(to, span.end);
    if (end <= start) continue;
    span.node.deleteData(start - span.start, end - start);
  }
}

/** Platzhalter an der Cursorstelle samt Zustand seiner Ausblende-Markierungen. */
export interface CaretProfileToken {
  parsed: ParsedProfileToken;
  /** Block, in dem der Platzhalter steht. Marker bleiben immer in diesem Block. */
  scope: HTMLElement;
  /** Positionen im Text des Blocks. */
  start: number;
  end: number;
  hideFieldWrapped: boolean;
  /** Grenzen von `[#` … `#]`, falls vorhanden. */
  wrapStart: number;
  wrapEnd: number;
}

/**
 * Textknoten eines Blocks ohne verschachtelte Blöcke (Tabellenzellen, Unterabsätze).
 * Ohne diese Grenze rutschen Marker in den nächsten Textknoten – im Zweifel also
 * hinter Tabelle und Bild statt direkt an den Platzhalter.
 */
function flattenBlockText(scope: HTMLElement): { text: string; spans: TextSpan[] } {
  const spans: TextSpan[] = [];
  let text = "";
  const walker = scope.ownerDocument.createTreeWalker(scope, NodeFilter.SHOW_TEXT, null);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const textNode = node as Text;
    if (lineContainerOf(textNode, scope) !== scope) continue;
    const data = textNode.data ?? "";
    spans.push({ node: textNode, start: text.length, end: text.length + data.length });
    text += data;
  }
  return { text, spans };
}

/** Block, in dem eine Cursorposition liegt. */
function blockScopeOf(node: Node, body: HTMLElement): HTMLElement {
  if (node === body) return body;
  return lineContainerOf(node, body);
}

/**
 * DOM-Position für eine Textposition des Blocks. „after“ bleibt am Ende des
 * vorangehenden Knotens, „before“ am Anfang des folgenden – sonst landet ein
 * Marker an einer Knotengrenze im falschen Element.
 */
function locateInBlock(
  spans: TextSpan[],
  offset: number,
  bias: "before" | "after"
): { node: Text; offset: number } | null {
  const usable = spans.filter((span) => span.end > span.start);
  if (usable.length === 0) return null;

  for (const span of usable) {
    const inside =
      bias === "after" ? offset > span.start && offset <= span.end : offset >= span.start && offset < span.end;
    if (inside) return { node: span.node, offset: offset - span.start };
  }

  const first = usable[0];
  if (offset <= first.start) return { node: first.node, offset: 0 };
  const last = usable[usable.length - 1];
  return { node: last.node, offset: last.node.data.length };
}

/** Textposition im Block für einen (Knoten, Offset)-Punkt der Auswahl. */
function blockOffsetOf(spans: TextSpan[], node: Node | null, offset: number): number | null {
  if (!node) return null;
  if (node.nodeType === Node.TEXT_NODE) {
    const span = spans.find((candidate) => candidate.node === node);
    return span ? span.start + Math.min(offset, span.node.data.length) : null;
  }

  const children = node.childNodes;
  if (children.length === 0) return null;
  if (offset < children.length) {
    const child = children[offset];
    const span = spans.find((candidate) => candidate.node === child || child.contains(candidate.node));
    return span ? span.start : null;
  }

  // Cursor hinter dem letzten Kind: Ende des letzten Textknotens darin.
  const child = children[children.length - 1];
  const inside = spans.filter((candidate) => candidate.node === child || child.contains(candidate.node));
  return inside.length > 0 ? inside[inside.length - 1].end : null;
}

function insertInBlock(scope: HTMLElement, offset: number, text: string, bias: "before" | "after"): boolean {
  const position = locateInBlock(flattenBlockText(scope).spans, offset, bias);
  if (!position) return false;
  position.node.insertData(position.offset, text);
  return true;
}

function deleteInBlock(scope: HTMLElement, from: number, to: number): boolean {
  const { spans } = flattenBlockText(scope);
  const start = locateInBlock(spans, from, "before");
  const end = locateInBlock(spans, to, "after");
  if (!start || !end) return false;
  if (start.node === end.node) {
    start.node.deleteData(start.offset, end.offset - start.offset);
    return true;
  }
  deleteFlatRange(spans, from, to);
  return true;
}

function replaceInBlock(scope: HTMLElement, from: number, to: number, text: string): boolean {
  const { spans } = flattenBlockText(scope);
  const start = locateInBlock(spans, from, "before");
  const end = locateInBlock(spans, to, "after");
  if (!start || !end) return false;
  if (start.node === end.node) {
    start.node.replaceData(start.offset, end.offset - start.offset, text);
    return true;
  }

  // Über Knotengrenzen: löschen und im Startknoten einfügen, der dabei erhalten bleibt.
  deleteFlatRange(spans, from, to);
  start.node.insertData(Math.min(start.offset, start.node.data.length), text);
  return true;
}

/** Setzt den Cursor an eine Textposition des Blocks. */
function placeCaretInBlock(scope: HTMLElement, offset: number): void {
  const position = locateInBlock(flattenBlockText(scope).spans, offset, "after");
  if (!position) return;
  const range = scope.ownerDocument.createRange();
  range.setStart(position.node, Math.min(position.offset, position.node.data.length));
  range.collapse(true);
  const selection = scope.ownerDocument.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

/** Markiert einen Textbereich des Blocks. */
function selectInBlock(scope: HTMLElement, from: number, to: number): void {
  const { spans } = flattenBlockText(scope);
  const start = locateInBlock(spans, from, "before");
  const end = locateInBlock(spans, to, "after");
  if (!start || !end) return;
  const range = scope.ownerDocument.createRange();
  range.setStart(start.node, Math.min(start.offset, start.node.data.length));
  range.setEnd(end.node, Math.min(end.offset, end.node.data.length));
  const selection = scope.ownerDocument.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

interface SelectionContext {
  scope: HTMLElement;
  text: string;
  /** Auswahlgrenzen im Text des Blocks; bei Cursor sind beide gleich. */
  from: number;
  to: number;
}

function readSelectionContext(body: HTMLElement): SelectionContext | null {
  const selection = body.ownerDocument.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!body.contains(range.commonAncestorContainer)) return null;

  const scope = blockScopeOf(range.startContainer, body);
  const { text, spans } = flattenBlockText(scope);
  const from = blockOffsetOf(spans, range.startContainer, range.startOffset);
  if (from === null) return null;
  // Endet die Auswahl in einem anderen Block, zählt nur die Startposition.
  const to = range.collapsed ? from : blockOffsetOf(spans, range.endContainer, range.endOffset) ?? from;
  return { scope, text, from, to };
}

/** Platzhalter, den die Auswahl genau abdeckt oder der den Cursor echt umschließt. */
function tokenAtSelection(text: string, from: number, to: number): { start: number; end: number; token: string } | null {
  const regex = profileTokenRegex();
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    const caretInside = from === to && from > start && from < end;
    const exactlySelected = from === start && to === end;
    if (caretInside || exactlySelected) return { start, end, token: match[0] };
  }
  return null;
}

/** Auswahl aus Text und genau einem Platzhalter, die sich klammern lässt. */
export interface ProfileSpanSelection {
  scope: HTMLElement;
  from: number;
  to: number;
  /** Steht direkt vor bzw. hinter der Auswahl schon `[#` … `#]`? */
  hideFieldWrapped: boolean;
}

/**
 * Prüft, ob die Auswahl genau einen vollständigen Platzhalter samt beliebigem
 * Text davor/danach umfasst. Angeschnittene Platzhalter und schon vorhandene
 * Klammerungen innerhalb der Auswahl schließen das aus, weil `[# … #]` nicht
 * verschachtelt werden darf.
 */
function spanSelectionAt(context: SelectionContext): ProfileSpanSelection | null {
  const { scope, text, from, to } = context;
  if (from >= to) return null;

  const inner = text.slice(from, to);
  if (inner.includes(HIDE_FIELD_OPEN) || inner.includes(HIDE_FIELD_CLOSE)) return null;

  const regex = profileTokenRegex();
  let match: RegExpExecArray | null;
  let tokens = 0;
  while ((match = regex.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    if (start >= to || end <= from) continue;
    if (start < from || end > to) return null;
    tokens += 1;
  }
  if (tokens !== 1) return null;

  const wrapped =
    text.slice(Math.max(0, from - HIDE_FIELD_OPEN.length), from) === HIDE_FIELD_OPEN &&
    text.slice(to, to + HIDE_FIELD_CLOSE.length) === HIDE_FIELD_CLOSE;
  return { scope, from, to, hideFieldWrapped: wrapped };
}

/** Steht der Cursor mitten in einem `[#`- oder `#]`-Marker? */
function markerAtOffset(text: string, offset: number): { start: number; end: number } | null {
  const previous = text[offset - 1];
  const next = text[offset];
  const inMarker =
    (previous === HIDE_FIELD_OPEN[0] && next === HIDE_FIELD_OPEN[1]) ||
    (previous === HIDE_FIELD_CLOSE[0] && next === HIDE_FIELD_CLOSE[1]);
  return inMarker ? { start: offset - 1, end: offset + 1 } : null;
}

/** `[#` … `#]` um eine Position, sofern ein Platzhalter dazwischen steht. */
function wrapperPairAt(text: string, offset: number): { start: number; end: number } | null {
  const open = text.lastIndexOf(HIDE_FIELD_OPEN, offset);
  const close = text.indexOf(HIDE_FIELD_CLOSE, Math.max(0, offset - 1));
  if (open < 0 || close < open + HIDE_FIELD_OPEN.length) return null;
  if (!profileTokenRegex().test(text.slice(open + HIDE_FIELD_OPEN.length, close))) return null;
  return { start: open, end: close + HIDE_FIELD_CLOSE.length };
}

/** Prüft, ob der Platzhalter von `[#` … `#]` umschlossen ist. */
function wrapStateAround(
  text: string,
  hit: { start: number; end: number }
): Pick<CaretProfileToken, "hideFieldWrapped" | "wrapStart" | "wrapEnd"> {
  const before = text.slice(0, hit.start);
  const openIndex = before.lastIndexOf(HIDE_FIELD_OPEN);
  const closedBefore = before.lastIndexOf(HIDE_FIELD_CLOSE);
  const after = text.slice(hit.end);
  const closeIndex = after.indexOf(HIDE_FIELD_CLOSE);
  const openAfter = after.indexOf(HIDE_FIELD_OPEN);
  const wrapped =
    openIndex >= 0 && openIndex > closedBefore && closeIndex >= 0 && (openAfter < 0 || closeIndex < openAfter);

  return {
    hideFieldWrapped: wrapped,
    wrapStart: wrapped ? openIndex : hit.start,
    wrapEnd: wrapped ? hit.end + closeIndex + HIDE_FIELD_CLOSE.length : hit.end,
  };
}

/**
 * Liefert den Platzhalter, auf den sich die Profilfeld-Aktionen beziehen: die
 * Auswahl muss ihn genau abdecken oder der Cursor mitten darin stehen.
 * Verändert nichts – auch nicht die Auswahl.
 */
export function findProfileTokenAtCaret(body: HTMLElement): CaretProfileToken | null {
  const context = readSelectionContext(body);
  if (!context) return null;
  const hit = tokenAtSelection(context.text, context.from, context.to);
  if (!hit) return null;
  const parsed = parseProfileToken(hit.token);
  if (!parsed) return null;
  return {
    parsed,
    scope: context.scope,
    start: hit.start,
    end: hit.end,
    ...wrapStateAround(context.text, hit),
  };
}

/** Zustand der Auswahl, wie ihn die Profilfeld-Schaltflächen brauchen. */
export type ProfileSelectionState =
  | { kind: "none" }
  /** Komplettes `[# … #]` markiert: nur noch löschen möglich. */
  | { kind: "wrapper" }
  /** Nur der Platzhalter markiert: ausblenden möglich. */
  | { kind: "token"; token: CaretProfileToken }
  /** Text plus genau ein Platzhalter markiert: nur Conditional range möglich. */
  | { kind: "span"; span: ProfileSpanSelection };

/**
 * Hält die Auswahl auf Platzhaltern „atomar“: ein Klick mitten in einen
 * Platzhalter markiert ihn komplett, ein Klick in einen `[#`/`#]`-Marker
 * markiert die ganze Klammerung. So ist kein Teilstück einzeln editierbar.
 */
export function normalizeProfileSelection(body: HTMLElement): ProfileSelectionState {
  const context = readSelectionContext(body);
  if (!context) return { kind: "none" };
  const { scope, text, from, to } = context;

  const pair = wrapperPairAt(text, from);
  if (from === to) {
    const marker = markerAtOffset(text, from);
    if (marker) {
      const bounds = pair ?? marker;
      selectInBlock(scope, bounds.start, bounds.end);
      return { kind: "wrapper" };
    }
  } else if (pair && from === pair.start && to === pair.end) {
    return { kind: "wrapper" };
  }

  const hit = tokenAtSelection(text, from, to);
  if (!hit) {
    const span = spanSelectionAt(context);
    return span ? { kind: "span", span } : { kind: "none" };
  }
  const parsed = parseProfileToken(hit.token);
  if (!parsed) return { kind: "none" };
  if (from !== hit.start || to !== hit.end) selectInBlock(scope, hit.start, hit.end);

  return {
    kind: "token",
    token: { parsed, scope, start: hit.start, end: hit.end, ...wrapStateAround(text, hit) },
  };
}

/** Schaltet die `|HL`-Endung des Platzhalters um; er bleibt danach markiert. */
export function toggleHideLineAtCaret(body: HTMLElement): boolean {
  const target = findProfileTokenAtCaret(body);
  if (!target) return false;

  const next = buildProfileToken(target.parsed.name, !target.parsed.hideLine);
  if (!replaceInBlock(target.scope, target.start, target.end, next)) return false;
  body.focus();
  selectInBlock(target.scope, target.start, target.start + next.length);
  return true;
}

/**
 * Klammert mit `[#` … `#]` bzw. entfernt eine vorhandene Klammerung. Geklammert
 * wird der markierte Platzhalter oder – wenn Text mit genau einem Platzhalter
 * markiert ist – die ganze Auswahl. Der Bereich bleibt in beiden Richtungen
 * markiert, damit sich die Klammerung mit einem zweiten Klick wieder aufheben lässt.
 */
export function toggleHideFieldAtCaret(body: HTMLElement): boolean {
  const target = findProfileTokenAtCaret(body);
  if (target) {
    return target.hideFieldWrapped
      ? unwrapHideField(body, target.scope, target.start, target.end, target.wrapStart, target.wrapEnd)
      : wrapHideField(body, target.scope, target.start, target.end);
  }

  const context = readSelectionContext(body);
  const span = context ? spanSelectionAt(context) : null;
  if (!span) return false;

  return span.hideFieldWrapped
    ? unwrapHideField(
        body,
        span.scope,
        span.from,
        span.to,
        span.from - HIDE_FIELD_OPEN.length,
        span.to + HIDE_FIELD_CLOSE.length
      )
    : wrapHideField(body, span.scope, span.from, span.to);
}

function wrapHideField(body: HTMLElement, scope: HTMLElement, from: number, to: number): boolean {
  insertInBlock(scope, to, HIDE_FIELD_CLOSE, "after");
  insertInBlock(scope, from, HIDE_FIELD_OPEN, "before");
  body.focus();
  selectInBlock(scope, from + HIDE_FIELD_OPEN.length, to + HIDE_FIELD_OPEN.length);
  return true;
}

function unwrapHideField(
  body: HTMLElement,
  scope: HTMLElement,
  from: number,
  to: number,
  wrapStart: number,
  wrapEnd: number
): boolean {
  // Von hinten entfernen, damit die vordere Position gültig bleibt.
  deleteInBlock(scope, wrapEnd - HIDE_FIELD_CLOSE.length, wrapEnd);
  deleteInBlock(scope, wrapStart, wrapStart + HIDE_FIELD_OPEN.length);
  body.focus();
  selectInBlock(scope, from - HIDE_FIELD_OPEN.length, to - HIDE_FIELD_OPEN.length);
  return true;
}

/**
 * Löscht die markierte Einheit vollständig: bei `[# … #]` die Klammerung samt
 * Inhalt, sonst den Platzhalter. Ist nur der Kern innerhalb einer Klammerung
 * markiert, passiert nichts – sonst blieben `[#`/`#]` allein zurück.
 */
export function deleteProfileSelection(body: HTMLElement): boolean {
  const context = readSelectionContext(body);
  if (!context) return false;
  const { scope, text, from, to } = context;

  const pair = wrapperPairAt(text, from);
  if (pair && from === pair.start && to === pair.end) {
    if (!deleteInBlock(scope, pair.start, pair.end)) return false;
    body.focus();
    placeCaretInBlock(scope, pair.start);
    return true;
  }

  const hit = tokenAtSelection(text, from, to);
  if (!hit || wrapStateAround(text, hit).hideFieldWrapped) return false;
  if (!deleteInBlock(scope, hit.start, hit.end)) return false;
  body.focus();
  placeCaretInBlock(scope, hit.start);
  return true;
}

/** Liefert true, wenn im Text mindestens ein Platzhalter ohne Wert steckt. */
function hasEmptyPlaceholder(text: string, values: Record<string, string>): boolean {
  TOKEN_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TOKEN_PATTERN.exec(text)) !== null) {
    if (!valueOf(values, match[1])) return true;
  }
  return false;
}

/** `[# … #]`-Bereiche auflösen: leerer Platzhalter darin entfernt den Bereich. */
function resolveHideFieldGroups(root: HTMLElement, values: Record<string, string>): void {
  // Nach jeder Änderung neu einlesen, weil sich die Textpositionen verschieben.
  for (let guard = 0; guard < 100; guard += 1) {
    const { text, spans } = flattenText(root);
    const open = text.indexOf(HIDE_FIELD_OPEN);
    if (open < 0) return;
    const close = text.indexOf(HIDE_FIELD_CLOSE, open + HIDE_FIELD_OPEN.length);
    // Ohne Gegenstück bleibt die Markierung stehen, statt Inhalt zu verschlucken.
    if (close < 0) return;

    const inner = text.slice(open + HIDE_FIELD_OPEN.length, close);
    if (hasEmptyPlaceholder(inner, values)) {
      deleteFlatRange(spans, open, close + HIDE_FIELD_CLOSE.length);
    } else {
      deleteFlatRange(spans, close, close + HIDE_FIELD_CLOSE.length);
      deleteFlatRange(spans, open, open + HIDE_FIELD_OPEN.length);
    }
  }
}

const LINE_CONTAINERS = new Set(["P", "DIV", "LI", "TD", "TH", "BLOCKQUOTE"]);
const REMOVABLE_CONTAINERS = new Set(["P", "DIV", "LI"]);

/** Nächster Block, innerhalb dessen `<br>` die Zeilen trennt. */
function lineContainerOf(node: Node, root: HTMLElement): HTMLElement {
  let current: Node | null = node.parentNode;
  while (current && current !== root) {
    if (current.nodeType === Node.ELEMENT_NODE && LINE_CONTAINERS.has((current as HTMLElement).tagName)) {
      return current as HTMLElement;
    }
    current = current.parentNode;
  }
  return root;
}

/** Direktes Kind des Containers, das den Knoten enthält. */
function childOfContainer(container: HTMLElement, node: Node): Node | null {
  let current: Node | null = node;
  while (current && current.parentNode && current.parentNode !== container) {
    current = current.parentNode;
  }
  return current && current.parentNode === container ? current : null;
}

function isBr(node: Node | null): boolean {
  return !!node && node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === "BR";
}

/** Enthält der Container noch sichtbaren Inhalt? */
function hasVisibleContent(el: HTMLElement): boolean {
  if ((el.textContent ?? "").trim()) return true;
  return !!el.querySelector("img, hr, table, br");
}

/** Entfernt die Zeile, in der der Knoten steht (inklusive umgebender `<br>`). */
function removeLineAround(node: Node, root: HTMLElement): void {
  const container = lineContainerOf(node, root);
  const anchor = childOfContainer(container, node);
  if (!anchor) return;

  let first: Node = anchor;
  while (first.previousSibling && !isBr(first.previousSibling)) first = first.previousSibling;
  let last: Node = anchor;
  while (last.nextSibling && !isBr(last.nextSibling)) last = last.nextSibling;

  const trailingBr = isBr(last.nextSibling) ? last.nextSibling : null;
  const leadingBr = isBr(first.previousSibling) ? first.previousSibling : null;

  const doomed: Node[] = [];
  let current: Node | null = first;
  while (current) {
    doomed.push(current);
    if (current === last) break;
    current = current.nextSibling;
  }
  // Nur einen der beiden Umbrüche mitnehmen, sonst rutschen Zeilen zusammen.
  if (trailingBr) doomed.push(trailingBr);
  else if (leadingBr) doomed.push(leadingBr);

  doomed.forEach((n) => n.parentNode?.removeChild(n));

  if (container !== root && REMOVABLE_CONTAINERS.has(container.tagName) && !hasVisibleContent(container)) {
    container.parentNode?.removeChild(container);
  }
}

/** Zeilen mit leerem `|HL`-Platzhalter entfernen (Altbestand `|RF` ebenfalls). */
function resolveHideLines(root: HTMLElement, values: Record<string, string>): void {
  for (let guard = 0; guard < 200; guard += 1) {
    const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let found: Text | null = null;
    let node: Node | null = walker.nextNode();
    while (node && !found) {
      const data = (node as Text).data ?? "";
      TOKEN_PATTERN.lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = TOKEN_PATTERN.exec(data)) !== null) {
        if (match[2] && !valueOf(values, match[1])) {
          found = node as Text;
          break;
        }
      }
      node = walker.nextNode();
    }
    if (!found) return;
    removeLineAround(found, root);
  }
}

/** Verbleibende Platzhalter durch ihren Wert ersetzen (leer, wenn unbekannt). */
function replaceTokens(root: HTMLElement, values: Record<string, string>): void {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const nodes: Text[] = [];
  let node: Node | null = walker.nextNode();
  while (node) {
    nodes.push(node as Text);
    node = walker.nextNode();
  }
  nodes.forEach((textNode) => {
    const data = textNode.data ?? "";
    if (!data.includes("{{")) return;
    TOKEN_PATTERN.lastIndex = 0;
    const next = data.replace(TOKEN_PATTERN, (_full, name: string) => valueOf(values, name));
    if (next !== data) textNode.data = next;
  });
}

/**
 * Wert für ein Link-Ziel. Bei „tel:“ fallen Leerzeichen weg (sonst wählt Outlook nur den
 * ersten Teil), sonst werden sie kodiert, damit mehrteilige Werte ein gültiges Ziel bleiben.
 */
function urlValue(value: string, forTel: boolean): string {
  const trimmed = value.trim();
  return forTel ? trimmed.replace(/\s+/g, "") : trimmed.replace(/\s+/g, "%20");
}

/** Enthält der Wert schon ein Schema (z. B. mySite), darf das Präfix nicht davor stehen. */
function collapseDuplicateScheme(href: string): string {
  return href.replace(/^(?:https?:\/\/|mailto:|tel:)(?=https?:\/\/|mailto:|tel:)/i, "");
}

/**
 * Platzhalter in Link-Zielen ersetzen – für jedes Schema, also mailto:{{mail}},
 * tel:{{mobilePhone}} oder https://{{mySite}}. Browser und Sanitizer kodieren die Klammern
 * teils als %7B/%7D, beides wird erkannt.
 */
function replaceTokensInHrefs(root: HTMLElement, values: Record<string, string>): void {
  root.querySelectorAll("a[href]").forEach((anchor) => {
    const href = anchor.getAttribute("href") ?? "";
    if (!href.includes("{{") && !/%7b/i.test(href)) return;
    const decoded = href.replace(/%7b/gi, "{").replace(/%7d/gi, "}");
    const forTel = /^tel:/i.test(decoded);
    TOKEN_PATTERN.lastIndex = 0;
    const replaced = decoded.replace(TOKEN_PATTERN, (_full, name: string) =>
      urlValue(valueOf(values, name), forTel)
    );
    const next = collapseDuplicateScheme(replaced);
    if (next !== href) anchor.setAttribute("href", next);
  });
}

/** Eine Klammer ohne Gegenstück (Tippfehler) soll den Empfänger nicht erreichen. */
function removeStrayMarkers(root: HTMLElement): void {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const nodes: Text[] = [];
  let node: Node | null = walker.nextNode();
  while (node) {
    nodes.push(node as Text);
    node = walker.nextNode();
  }
  nodes.forEach((textNode) => {
    const data = textNode.data ?? "";
    if (!data.includes(HIDE_FIELD_OPEN) && !data.includes(HIDE_FIELD_CLOSE)) return;
    textNode.data = data.split(HIDE_FIELD_OPEN).join("").split(HIDE_FIELD_CLOSE).join("");
  });
}

/**
 * Ersetzt Platzhalter und löst dabei Conditional range und Hide line auf.
 * Werte landen als Textknoten im DOM und werden dadurch sicher escaped.
 */
export function renderProfilePlaceholders(html: string, values: Record<string, string>): string {
  if (!html) return html;
  const root = document.createElement("div");
  root.innerHTML = html;
  resolveHideFieldGroups(root, values);
  resolveHideLines(root, values);
  replaceTokens(root, values);
  replaceTokensInHrefs(root, values);
  removeStrayMarkers(root);
  return root.innerHTML;
}
